import { type FormContainerVariantType, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import {
  type DataBindingSourceInfo,
  type FormContainerModelField,
  mergeColumns,
  type MergeColumnsConfig,
  type PregenerationType,
} from '@blaze-shared/elements-domain';
import { type DatasetColumn } from '@blaze-shared/schema-domain';

import {
  FORM_PADDING_DRAGGED,
  FORM_PADDING_GENERATED,
  type FormContainerPropsModel,
} from '../../../elements/form-container/form-container.model';
import {
  getNewFormFieldVisibility,
  pregenerationTypeToAction,
} from '../../../utils/elements/generation.utils';

import { type FormLikeElement } from './form-generation-data';

/** How the form's model is reconciled with the columns of its data source. */
const FORM_MODEL_MERGE_CONFIG: MergeColumnsConfig<FormContainerModelField, 'fieldId', 'field'> = {
  keyId: 'fieldId',
  keyName: 'field',
  fieldsToUpdate: ['columnType', 'dataType'],
  mapColumnFn: datasetColumnToFormField,
  initAppendedColumnFn,
  shouldAlwaysKeepColumnFn: (column) => column.isCustom === true, // custom columns should always be kept
  sanitizeColumn: sanitizeFormFieldConditions,
};

/** What the generation writes into the form element itself, as opposed to into its fields. */
export interface FormPropsGenerationData {
  formElement: FormLikeElement | undefined;
  pregenerationType: PregenerationType | undefined;
  formTitle: string | undefined;
  inputDataSource: DataBindingSourceInfo | undefined;
  isNewDataSource: boolean | undefined;
  sameDataSourceDifferentView: boolean | undefined;
  accountsTableName: string | undefined;
}

/**
 * Builds the props of the form element: its origin and title when it is being created, and
 * the model reconciled with the data source's columns whenever there is one.
 */
export function generateFormProps({
  formElement,
  pregenerationType,
  formTitle,
  inputDataSource,
  isNewDataSource,
  sameDataSourceDifferentView,
  accountsTableName,
}: FormPropsGenerationData): PartialPropsOf<FormContainerVariantType> {
  const initProps: PartialPropsOf<FormContainerVariantType> = {
    content: {},
    wrapper: { refs: {} },
  };

  // Pregenerated form initialization (separate page or dialog)
  // Missing form element means that it's being created right now through pregeneration (not by dragging)
  if (!formElement) {
    if (!pregenerationType) {
      throw new Error('Pregeneration type missing');
    }

    // Set origin as generated
    if (initProps.content) {
      // Store the original pregenerationType
      initProps.content.origin = 'generated';
      initProps.content.pregenerationType = pregenerationType;
      initProps.content.action = pregenerationTypeToAction(pregenerationType);
    }

    // If title missing and it's initial form creation -> apply default
    if (!formTitle) {
      formTitle =
        pregenerationType === 'view' || pregenerationType === 'view-dialog' ? 'Details' : 'Form';
    }
    if (pregenerationType !== 'delete') {
      initProps.style = {
        container: {
          offset: {
            padding: pregenerationType === 'view' ? FORM_PADDING_DRAGGED : FORM_PADDING_GENERATED,
            syncEdit: false,
          },
        },
      } as FormContainerPropsModel['style'];
    }
  }

  if (formTitle && initProps.content) {
    initProps.content.title = {
      enabled: true,
      label: stripTrailingNumber(formTitle),
      align: 'left',
    };
  }

  // If new data source info missing, do not add data related props
  if (!inputDataSource) {
    return initProps;
  }
  if (!inputDataSource?.id) {
    throw new Error('Data source missing');
  }

  // If action is present - ignore pregenerationType
  const action =
    formElement?.props?.content?.action || pregenerationTypeToAction(pregenerationType);

  // Update model/fields
  if (initProps.content) {
    initProps.content.action = action;
    initProps.content.model = mergeColumns(
      { ...FORM_MODEL_MERGE_CONFIG, accountsTableName },
      inputDataSource.columns,
      keptFields(formElement, action),
      isNewDataSource || false,
      { preserveVisibility: sameDataSourceDifferentView },
    );
  }

  // Prepare data bindings for initial form pregeneration
  // TODO: think whether this is needed - in new pregeneration -> we populate the data source
  // const previousDataBinding = hasDataProps(formElement) ? formElement?.props?.data?.find(d => d.key === 'value') : undefined;
  if (!formElement) {
    initProps.data = [
      {
        enabled: true,
        key: 'value',
        dataSource: { ...inputDataSource },
        bindings: {},
      },
    ];
  }

  return initProps;
}

/** Fields the form keeps across the merge; a non-custom form drops the custom ones. */
function keptFields(
  formElement: FormLikeElement | undefined,
  action: string | undefined,
): FormContainerModelField[] | undefined {
  const currentFields = formElement?.props?.content?.model;
  return action !== 'custom' && currentFields
    ? currentFields.filter((field) => field.isCustom !== true)
    : currentFields;
}

/** why: a dragged-in title ends with the element's ordinal ("Form 2"), which is not a title */
function stripTrailingNumber(formTitle: string): string {
  return /.+ [0-9]+$/gi.test(formTitle)
    ? formTitle.substring(0, formTitle.lastIndexOf(' '))
    : formTitle;
}

function datasetColumnToFormField(
  col: DatasetColumn,
  accountsTableName?: string,
): FormContainerModelField {
  return {
    fieldId: col.id,
    field: col.name,
    value: null,
    dataType: col.dataType,
    controlType: '',
    columnType: col.columnType,
    // if account col => hide the field by default, otherwise let further logic decide
    visible: getNewFormFieldVisibility(col, accountsTableName),
    isRequired: col.isRequired,
  };
}

function initAppendedColumnFn(column: FormContainerModelField): FormContainerModelField {
  return { ...column, visible: false };
}

/** A field the schema requires keeps only its visibility and validation conditions. */
function sanitizeFormFieldConditions(
  mergedColumn: FormContainerModelField,
  colToMerge: FormContainerModelField,
): void {
  if (colToMerge.isRequired && mergedColumn.conditions?.length) {
    const conditions = mergedColumn.conditions.filter(
      (c) => !c.type || c.type === 'visibility' || c.type === 'validation',
    );
    mergedColumn.conditions = conditions.length ? conditions : undefined;
  }
}
